import type {
  ApiResponse,
  GraffitiListParams,
  GraffitiSearchParams,
  GraffitiSearchResponse,
} from '@/types/api'
import type { GraffitiFromAPI, GraffitiLien } from '@/types/entities'
import { apiDelete, apiGet, apiPost, apiPut } from './http'

// API des graffitis
export const graffitisApi = {
  /**
   * Récupérer la liste des graffitis avec pagination et filtres
   */
  async list(params?: GraffitiListParams): Promise<ApiResponse<GraffitiFromAPI[]>> {
    // console.log('[GraffitisAPI] list:', params)
    return apiGet<ApiResponse<GraffitiFromAPI[]>>(
      '/graffitis',
      params as Record<string, string | number | undefined>
    )
  },

  /**
   * Récupérer un graffiti par son ID avec ses liens et thématiques
   */
  async getById(id: number): Promise<GraffitiFromAPI> {
    console.log('[GraffitisAPI] getById:', id)
    return apiGet<GraffitiFromAPI>(`/graffitis/${id}`)
  },

  /**
   * Récupérer un graffiti complet avec toutes ses relations (images, références, liens)
   * Optimisé pour réduire le nombre de requêtes
   */
  async getByIdComplete(id: number): Promise<{
    graffiti: GraffitiFromAPI
    images: any[]
    references: any[]
    links: any[]
  }> {
    console.log('[GraffitisAPI] getByIdComplete:', id)
    const response = await apiGet<{
      data: {
        graffiti: GraffitiFromAPI
        images: any[]
        references: any[]
        links: any[]
      }
    }>(`/graffitis/${id}/complete`)
    return response.data
  },

  /**
   * Recherche avancée de graffitis
   */
  async search(params: GraffitiSearchParams): Promise<GraffitiSearchResponse> {
    console.log('[GraffitisAPI] search:', params)

    const searchParams: Record<string, string> = {}

    if (params.q) searchParams.q = params.q
    if (params.types?.length) searchParams.types = params.types.join(',')
    if (params.categories?.length) searchParams.categories = params.categories.join(',')
    if (params.thematiques?.length) searchParams.thematiques = params.thematiques.join(',')

    return apiGet<GraffitiSearchResponse>('/graffitis/search', searchParams)
  },

  /**
   * Récupérer tous les graffitis (pour compatibilité)
   * Utilise une limite élevée pour récupérer toutes les données en un appel
   */
  async getAll(): Promise<GraffitiFromAPI[]> {
    //console.log('[GraffitisAPI] getAll - Récupération de tous les graffitis')

    // Utiliser une limite élevée pour récupérer tous les graffitis en un appel
    const response = await this.list({ page: 1, limit: 1000 })

    // console.log(`[GraffitisAPI] getAll - ${response.data.length} graffitis récupérés`)
    return response.data
  },

  /**
   * Récupérer toutes les catégories distinctes
   */
  async getCategories(): Promise<string[]> {
    console.log('[GraffitisAPI] getCategories')
    const allGraffitis = await this.getAll()
    const categories = [...new Set(allGraffitis.map(g => g.categorie).filter(Boolean))]
    return categories.sort()
  },

  /**
   * Récupérer toutes les langues distinctes
   */
  async getLanguages(): Promise<string[]> {
    console.log('[GraffitisAPI] getLanguages')
    const allGraffitis = await this.getAll()
    const langues = [
      ...new Set(allGraffitis.map(g => g.langue).filter((l): l is string => Boolean(l))),
    ]
    return langues.sort()
  },

  /**
   * Mettre à jour un graffiti
   */
  async update(id: number, data: Partial<GraffitiFromAPI>): Promise<ApiResponse<GraffitiFromAPI>> {
    console.log('[GraffitisAPI] update:', id, data)
    return apiPut<ApiResponse<GraffitiFromAPI>>(`/graffitis/${id}`, data)
  },

  /**
   * Récupérer les liens d'un graffiti (sources et cibles)
   */
  async getLinks(
    id: number
  ): Promise<ApiResponse<{ sources: GraffitiLien[]; cibles: GraffitiLien[] }>> {
    console.log('[GraffitisAPI] getLinks:', id)
    return apiGet<ApiResponse<{ sources: GraffitiLien[]; cibles: GraffitiLien[] }>>(
      `/graffitis/${id}/liens`
    )
  },

  /**
   * Créer un lien entre deux graffitis
   */
  async createLink(
    id: number,
    data: {
      cible: number
      typeLien: string
      degLien: number
      commentaireLien?: string | null
    }
  ): Promise<ApiResponse<GraffitiLien>> {
    console.log('[GraffitisAPI] createLink:', id, data)
    return apiPost<ApiResponse<GraffitiLien>>(`/graffitis/${id}/liens`, data)
  },

  /**
   * Mettre à jour un lien
   */
  async updateLink(
    id: number,
    lienId: number,
    data: {
      source?: number
      cible?: number
      typeLien?: string
      degLien?: number
      commentaireLien?: string | null
    }
  ): Promise<ApiResponse<GraffitiLien>> {
    console.log('[GraffitisAPI] updateLink:', id, lienId, data)
    return apiPut<ApiResponse<GraffitiLien>>(`/graffitis/${id}/liens/${lienId}`, data)
  },

  /**
   * Supprimer un lien
   */
  async deleteLink(id: number, lienId: number): Promise<void> {
    console.log('[GraffitisAPI] deleteLink:', id, lienId)
    return apiDelete<void>(`/graffitis/${id}/liens/${lienId}`)
  },
}
